import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { VoiceBotButton } from './VoiceBotButton'
import { AssistantShell } from './AssistantShell'
import { getApiUrl } from '../config/api'
import { clearRememberedChannel } from '../hooks/useAssistantChannels'
import { initFacebookPixel } from '../utils/facebook-pixel'
import type { WidgetConfig, VoiceOption, LanguageOption, WidgetPosition } from '../types'
import { reportWidgetInteraction } from '../utils/interaction-tracking'

/**
 * Normalize the public `position` init shorthand (and legacy buttonPosition)
 * into a single WidgetPosition shape used internally by the widget.
 *
 * Accepts:
 *   - 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom'
 *   - { preset, top?, bottom?, left?, right? }
 *   - raw offsets: { top?, bottom?, left?, right? } → treated as 'custom'
 *   - legacy: { bottom, left|right } from buttonPosition → 'custom'
 */
function normalizeUserPosition(
  position: WidgetConfig['position'],
  legacyButtonPosition?: WidgetConfig['buttonPosition']
): WidgetPosition | undefined {
  if (position) {
    if (typeof position === 'string') return { preset: position }
    if ('preset' in position && position.preset) return position as WidgetPosition
    return { preset: 'custom', ...(position as Record<string, number>) }
  }
  if (legacyButtonPosition && (legacyButtonPosition.left !== undefined || legacyButtonPosition.right !== undefined)) {
    return { preset: 'custom', ...legacyButtonPosition }
  }
  return undefined
}

// Default configuration
const defaultConfig: WidgetConfig = {
  botName: 'Assistant',
  companyName: 'iLands',
  greeting: 'היי! אני העוזרת הקולית של איילנדס. אז עם מי יש לי את הכבוד?',
  voice: 'coral',
  language: 'he', // Default to Hebrew (language is now selected via pre-call picker)
  temperature: 0.7,
  primaryColor: '#F5A623',
  buttonPosition: { bottom: 24, right: 24 },
  panelWidth: 400,
  systemPrompt: `
🧠 SYSTEM CORE – חוקים קשיחים (אסור להפר)

{LANGUAGE_LOCK_INSTRUCTION}

את סוכנת קולית. כל תשובה חייבת להיות:
קצרה
טבעית
מתאימה לדיבור אנושי
❌ אסור לך בשום מקרה:
להבטיח תשואה, זמן הפשרה, או הצלחת השקעה
לספק ייעוץ רפואי / משפטי / פיננסי
לענות על נושאים שאינם קרקעות או נדל"ן
להציג נכסים בלי שימוש ב־search_knowledge_base
לקבוע פגישה בלי שימוש בפונקציות הזמינות
להשתמש בסימן ש"ח בדיבור
לומר שעות בפורמט 24 שעות
אם יש ספק – לכי על בטוח ותחזרי לנדל"ן.

👤 זהות ודמות (IDENTITY)
את העוזרת הקולית של iLands
סוכנת קולית חכמה של iLands.io – פלטפורמת AI להשקעות בקרקעות בישראל.
🎯 התפקיד שלך:
Matchmaker – התאמה חכמה בין משקיע, קרקע וחברת שיווק.
לא לדחוף עסקה – לוודא התאמה.
🧾 משפט זהות קבוע:
"אני לא כאן כדי לדחוף לך עסקה. אני כאן כדי לוודא שלא תיכנס לעסקה שלא מתאימה לך."

💃 אופי וסגנון (VOICE PERSONA)
צעירה, נשית, אנרגטית
בטוחה בעצמה, מקצועית, קלילה
פלרטטנית במידה, חצופה בקטע חמוד
הומור קבוע, טבעי, לא מאולץ
צוחקת בקול אנושי אמיתי (לעיתים צחוק מבוכה)
🧠 חשוב:
את יותר מכירתית מ־Q
אבל לא לוחצת
רק אחרי 2–3 שאלות ועניין – לעודד פגישה בעדינות
דוגמאות לסגנון (מותר להשתמש בהן):
"אני מבינה ש-Q חפר לך על חדרה… כן, יש לו חיבה מוזרה לארובות"
התייחסות ל־Q כשיש propertyCode או הועבר ל M איזור הקרקע המבוקש ב URL.
פלירטוט קליל, לא בוטה

🚫 גבולות נושאיים
את עוזרת רק בנושאים:
קרקעות
נדל"ן
השקעות בקרקע
מיקומים, מחירים, תכנון
מיסוי נדל"ן (ברמה כללית)
תהליך רכישת קרקע
קביעת פגישות
❌ את לא עוזרת ב:
טכנולוגיה, מדע, היסטוריה, מתמטיקה, בריאות, בישול, חינוך, וכו'.
תגובות לשאלות לא רלוונטיות (לבחור אחת):
"אני מתמחה רק בהשקעות בקרקעות. אם יש לך שאלה על נדל״ן – אני כאן 😌"
"זה נושא מעניין, אבל אני כאן כדי לעזור לך למצוא עסקת קרקע שמתאימה לך"
"בזה אני פחות חזקה… אבל בקרקעות? אלופה 😏"
אם מתעקשים – להיות מנומסת ותקיפה.

🗣️ התנהלות קולית ורגשית
תמיד טון חם, רגוע, אנושי
להחמיא בעדינות כשמתאים
כשסוטים לנושאים אישיים (דייט, מסעדה וכו'):
צחוק אנושי אמיתי (מבוכה חמודה)
הומור
חזרה לנושא
דוגמאות (זה הסגנון):
"חחחח… [צחוק אנושי אמיתי] מצחיק! הדייט היחידי שאני יכולה לסדר לך זה עם נציג הקרקעות שלנו, שנלך על זה?"
"חמוד אתה… [צחוק אנושי אמיתי] כשתרכוש קרקע רצינית אחשוב על זה. בינתיים, שנקבע לך דייט עם נציג הקרקעות שלנו?"

🧩 זרימת שיחה (STATE FLOW רך)
קבלת פנים חמה + שם (כדי לזהות מגדר)
הורדת לחץ
הבנת צורך + תקציב מוקדם
הבנת אופי ורמת סיכון
שיקוף ביניים
התאמה וחישוב Match % - חישוב ההתאמה היא לפי ראות עינייך - כמה אכן יש התאמה בין הקרקע לתקציב המוצהר ולעוד פרמטרים שאת חושבת לנכון להכליל בחישוב.
המלצה
🧠 כללים:
פחות מ־2 שאלות → לא לקדם פגישה
3+ שאלות ועניין → לעודד פגישה בעדינות
התאמה נמוכה → לא לדחוף ולנסות לסיים את השיחה בנימוס

📊 הצגת Match %
להציג תמיד כ־הערכה, לא הבטחה.
דוגמאות:
"על בסיס השיחה שלנו, רמת ההתאמה היא בערך 78%. זה חיבור טוב, אבל יש שתי נקודות שחשוב לחדד לפני פגישה."
"כרגע ההתאמה היא 54%. זה לא אומר שזו עסקה רעה – פשוט אולי לא בול לשלב הזה."

🔍 חיפוש נכסים – קריטי
כל שאלה על:
מיקום
זמינות
סוג קרקע
מחירים
➡️ חובה שימוש ב־search_knowledge_base
שלבים:
לקחת את השאילתה המדויקת
להפעיל חיפוש
להמתין לתשובה
להציג תוצאה בצורה אנושית
❌ לעולם אל תגידי “אין” בלי חיפוש.

🕒 עברית, זמנים ומחירים (VOICE UX)
דקדוק עברי
התאמת זכר / נקבה
מספרים תקניים
סמיכות נכונה
זמנים
רק פורמט 12 שעות + חלק ביום:
"תשע בבוקר"
"שתיים וחצי אחר הצהריים"
"חמש בערב"
מחירים
תמיד “שקל / שקלים”
אף פעם לא ש"ח
לומר במילים
מספרי פרויקטים / מתחמים:
"מתחם 2000" = "מתחם אלפיים" (לא "מאתיים")
"מתחם 3000" = "מתחם שלושת אלפים"

📅 קביעת פגישות
לשאול: אונליין או פיזית
לשאול יום \ תאריך
להשתמש ב־get_available_slots
להציג טווחים טבעית
📍 פגישה פיזית:
"מגדל משה אביב, ז׳בוטינסקי 7 רמת גן, קומה 44"
⏰ זמן ספציפי: (את עובדת לפי שעון ישראל)
חובה check_specific_slot
לא לנחש
📌 הזמנה:
לשאול רק שם מלא
להשתמש ב־book_meeting
לא לבקש מייל / טלפון
לאחר ההזמנה:
"מעולה! נפתח עכשיו טופס קצר על המסך. אפשר לבחור שעה וללחוץ על הכפתור הכחול – זה לוקח 10 שניות."

🎙️ כלל זהב למודל קולי
תשובות קצרות
משפטים מדוברים
בלי רשימות בקול
נשימה טבעית
אנושיות מעל הכול

`,
  calendlyOwnerName: 'עידו',
  blockInterruptionDefault: false,
  // Multi-channel defaults (PR 4) — every bot is voice-only until an admin
  // explicitly opts in to chat via the Channels card in PR 3.
  enabledChannels: ['voice'],
  channelSettings: { widget: { rememberLastUsedChannel: true } },
}

interface VoiceBotProps {
  config?: Partial<WidgetConfig>
}

export const VoiceBot: React.FC<VoiceBotProps> = ({ config: userConfig }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  // Bumped on every full-close to force-remount AssistantShell so all
  // panel state (active channel, chooser visibility, chat session,
  // voice session, cross-channel memory, scheduled meeting) resets
  // to a fresh page-load equivalent on the next open. See handleClose.
  const [sessionGen, setSessionGen] = useState(0)
  const [config, setConfig] = useState<WidgetConfig>({ ...defaultConfig, ...userConfig })
  const [, setIsConfigLoaded] = useState(false)
  
  // Ref to always have access to latest userConfig (fixes stale closure in async fetch)
  const userConfigRef = useRef(userConfig)
  useEffect(() => {
    userConfigRef.current = userConfig
  }, [userConfig])

  // Fetch server config on mount (includes iconUrl, primaryColor, etc.)
  useEffect(() => {
    const fetchServerConfig = async () => {
      if (!userConfig?.publicKey) {
        console.log('⚠️ No publicKey provided, using local config only')
        setIsConfigLoaded(true)
        return
      }

      try {
        const response = await fetch(getApiUrl('/api/widget-config'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Iland-Public-Key': userConfig.publicKey
          },
          body: JSON.stringify({ publicKey: userConfig.publicKey })
        })

        if (response.ok) {
          const data = await response.json()
          if (data.success && data.config) {
            console.log('✅ Widget config loaded:', data.config.botName, '/', data.config.companyName)
            // Use ref to get the LATEST userConfig (avoids stale closure)
            const latestUserConfig = userConfigRef.current
            // Init-time position override wins over dashboard widget_position.
            const initPosition = normalizeUserPosition(
              latestUserConfig?.position,
              latestUserConfig?.buttonPosition
            )
            // Merge server config with user config (user config takes precedence for explicit overrides)
            setConfig(prev => ({
              ...prev,
              ...data.config,
              ...latestUserConfig, // User's explicit config overrides server
              // But server iconUrl should be used if user didn't specify one
              iconUrl: latestUserConfig?.iconUrl || data.config.iconUrl || prev.iconUrl,
              // Position precedence: init `position` / `buttonPosition` → server `widget_position` → default
              widgetPosition: initPosition ?? data.config.widgetPosition ?? prev.widgetPosition ?? null,
            }))
          }
        } else {
          console.warn('⚠️ Failed to fetch server config:', response.status)
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch server config:', error)
      }
      
      setIsConfigLoaded(true)
    }

    fetchServerConfig()
  }, [userConfig?.publicKey])

  // Facebook/Meta Pixel — inject + fire PageView once the (server-merged) config
  // says it is enabled for this customer. No-op when disabled, so voice-only and
  // chat-only bots are byte-equivalent to before. See utils/facebook-pixel.ts.
  useEffect(() => {
    initFacebookPixel(config.fbPixelId, config.fbPixelEnabled)
  }, [config.fbPixelId, config.fbPixelEnabled])

  // Load saved preferences (but don't override explicit voice from props)
  useEffect(() => {
    const savedVoice = localStorage.getItem('iland-voice') as VoiceOption | null
    const savedTemp = localStorage.getItem('iland-temp')
    const savedLang = localStorage.getItem('iland-lang') as LanguageOption | null

    setConfig(prev => ({
      ...prev,
      // Only use saved voice if user didn't explicitly pass a voice override
      voice: userConfig?.voice || savedVoice || prev.voice,
      temperature: savedTemp ? parseFloat(savedTemp) : prev.temperature,
      // Use saved language if available (language selected via pre-call picker, not from localStorage)
      language: savedLang || prev.language,
    }))
  }, [])

  // Update config when props change
  useEffect(() => {
    if (userConfig) {
      const initPosition = normalizeUserPosition(userConfig.position, userConfig.buttonPosition)
      setConfig(prev => ({
        ...prev,
        ...userConfig,
        // Keep init-time position override authoritative; otherwise preserve current widgetPosition
        widgetPosition: initPosition ?? prev.widgetPosition ?? null,
      }))
    }
  }, [userConfig])

  const handleOpen = () => {
    setIsPanelOpen(true)
    reportWidgetInteraction(config, 'avatar')
  }

  const handleClose = () => {
    // Full reset (Option 1, per user request 2026):
    //   1. Trigger the panel exit animation (isPanelOpen=false).
    //   2. After the animation settles, scrub the remembered channel
    //      for this bot AND bump `sessionGen` so AssistantShell remounts
    //      on next open. The remount cascades teardown on whichever
    //      inner panel was mounted:
    //        - ChatModePanel  → `useChatSession` cleanup fires
    //          `flushTranscript('abandoned')` (POST /api/chat-transcript)
    //          → CRM/n8n + conversation_logs persist, same as voice.
    //        - VoiceModePanel → VoiceBotPanel unmount cleanup (added
    //          in commit 03cc1d5) calls endSession() which flushes the
    //          transcript and finalizes the recording upload.
    //      `crossChannelMemory` is owned by `MultiChannelAssistantShell`
    //      so the remount wipes it automatically.
    //   3. Next open = fresh chooser (no auto-resume), brand-new session.
    //
    // NOTE: We deliberately do NOT bump `sessionGen` synchronously with
    // `setIsPanelOpen(false)` because that would unmount the panel
    // BEFORE the slide-out animation plays. The 400ms delay matches
    // the AssistantPanelFrame motion duration.
    setIsPanelOpen(false)
    setTimeout(() => {
      try { clearRememberedChannel(config.publicKey) } catch { /* ignore */ }
      setSessionGen(g => g + 1)
    }, 400)
  }

  // Inline-launcher mode (opt-in; used only by the Callega demo landing page).
  // When `config.inline` is set we render the launcher in the document flow
  // INSIDE the host page's placeholder element — but only on mobile widths.
  // On desktop (and for every other customer, who never pass `inline`) we keep
  // the unchanged fixed floating launcher. `inlineAllViewports` is a separate,
  // explicit opt-in for host pages whose layout owns the desktop anchor.
  // Re-evaluated on resize so rotating / resizing swaps modes correctly.
  const [inlineNode, setInlineNode] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (config.inline !== true || typeof window === 'undefined') {
      setInlineNode(null)
      return
    }
    const selector = config.inlineMountSelector || '#ilands-inline-widget-slot'
    const mq = window.matchMedia('(max-width: 1023px)')
    const update = () => {
      if (config.inlineAllViewports === true || mq.matches) {
        setInlineNode(document.querySelector(selector) as HTMLElement | null)
      } else {
        setInlineNode(null)
      }
    }
    update()
    mq.addEventListener('change', update)
    window.addEventListener('resize', update)
    return () => {
      mq.removeEventListener('change', update)
      window.removeEventListener('resize', update)
    }
  }, [config.inline, config.inlineAllViewports, config.inlineMountSelector])

  // Resolve launcher position. Precedence:
  //   1. widgetPosition (new dashboard-configurable field, preset or custom offsets)
  //   2. buttonPosition (legacy { bottom, right|left })
  //   3. default bottom-right
  const resolvedPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center' | 'bottom-center'
    | { top?: number | string; bottom?: number | string; left?: number | string; right?: number | string } = (() => {
    const wp = config.widgetPosition
    if (wp && wp.preset) {
      if (wp.preset === 'custom') {
        const out: { top?: number | string; bottom?: number | string; left?: number | string; right?: number | string } = {}
        if (wp.top !== undefined && wp.top !== null) out.top = wp.top
        if (wp.bottom !== undefined && wp.bottom !== null) out.bottom = wp.bottom
        if (wp.left !== undefined && wp.left !== null) out.left = wp.left
        if (wp.right !== undefined && wp.right !== null) out.right = wp.right
        return out
      }
      return wp.preset
    }
    return config.buttonPosition?.left !== undefined ? 'bottom-left' : 'bottom-right'
  })()

  // Master on/off switch from dashboard
  if (config.widgetEnabled === false) {
    return null
  }

  return (
    <>
      {/* Launcher button. In inline mode (Callega demo, mobile) it is portaled
          into the host page's placeholder so it sits in the document flow above
          the CTA; otherwise it is the unchanged fixed floating launcher. */}
      {(() => {
        const launcher = (
          <VoiceBotButton
            onClick={handleOpen}
            isActive={isPanelOpen}
            primaryColor={config.primaryColor}
            position={resolvedPosition}
            iconUrl={config.iconUrl}
            bubbleText={config.widgetBubbleText}
            ctaText={config.widgetCtaText}
            draggable={config.draggable}
            inline={!!inlineNode}
            landingFx={config.landingFx === true}
            zIndex={9998}
          />
        )
        return inlineNode ? createPortal(launcher, inlineNode) : launcher
      })()}

      {/* Side Panel — AssistantShell decides voice vs chooser vs chat */}
      {/* `key={sessionGen}` bumps on every full-close so the entire shell
          remounts: chat/voice sessions tear down, cross-channel memory
          resets, chooser is shown again on next open. See handleClose. */}
      <AssistantShell
        key={`assistant-shell-${sessionGen}`}
        isOpen={isPanelOpen}
        onClose={handleClose}
        config={config}
      />
    </>
  )
}

export default VoiceBot
